import { graphql, useFragment } from "react-relay";
import { FormattedMessage, FormattedNumber } from "react-intl";
import { CompetitionScoringCriteriaFragment$key } from "./__generated__/CompetitionScoringCriteriaFragment.graphql";

import { Pencil01Icon } from "@/icons";
import { Link } from "@/kit/ui/link";

const CompetitionScoringCriteriaFragment = graphql`
  fragment CompetitionScoringCriteriaFragment on Competition {
    slug
    viewerCanEdit: viewerCan(action: UPDATE_COMPETITION)
    scoringCriteria {
      id
      title
      description
      weight
    }
  }
`;

interface Props {
  competition: CompetitionScoringCriteriaFragment$key;
}

export default function CompetitionScoringCriteria({
  competition: competitionFragment,
}: Props) {
  const { scoringCriteria, slug, viewerCanEdit } = useFragment(
    CompetitionScoringCriteriaFragment,
    competitionFragment,
  );
  const editLocation = `/competitions/${slug}/scoring-criteria`;

  if (
    scoringCriteria.every(
      ({ description, weight }) => !description && weight == 1,
    )
  ) {
    if (viewerCanEdit) {
      return (
        <div>
          <Link variant="secondary" size="sm" to={editLocation}>
            <Pencil01Icon />
            <FormattedMessage defaultMessage="Edit Scoring Criteria" />
          </Link>
        </div>
      );
    } else {
      return null;
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold w-full font-poppins">
          <FormattedMessage defaultMessage="Scoring Criteria" />
        </h1>
        {viewerCanEdit && (
          <Link variant="secondary" size="sm" to={editLocation}>
            <Pencil01Icon />
            <FormattedMessage defaultMessage="Edit" />
          </Link>
        )}
      </div>
      <div className="overflow-x-auto">
        <table className="table-auto w-full">
          <thead>
            <tr>
              <th className="p-2 bg-gray-100">
                <FormattedMessage defaultMessage="Name" />
              </th>
              <th className="p-2 bg-gray-100">
                <FormattedMessage defaultMessage="Description" />
              </th>
              <th className="p-2 bg-gray-100">
                <FormattedMessage defaultMessage="Weight" />
              </th>
            </tr>
          </thead>
          <tbody>
            {scoringCriteria.map(({ id, title, description, weight }) => (
              <tr key={id}>
                <td className="p-2 text-center">{title}</td>
                <td className="p-2">{description}</td>
                <td className="p-2 text-center">
                  <FormattedNumber value={weight * 100} />%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
